import { Injectable } from '@angular/core';
import { BlueCityService } from '@ui/shared/util-api';
import { FilterDataStatus, FilterOption } from '@ui/shared/util-filter';
import { BehaviorSubject, Observable, Subject, catchError, concat, map, of, switchMap } from 'rxjs';
import { Result, ToResult, toDataResult } from '@team23/ts-result';

type FilterDataResult = Result<Array<FilterOption>, FilterDataStatus>;

const loadingStatus = ToResult.fromErrorKey(FilterDataStatus.Loading);

@Injectable({ providedIn: 'root' })
export class BlueCityFilterDataService {
    public categoriesOptions$ = new BehaviorSubject<FilterDataResult>(loadingStatus);
    public sponsorsOptions$ = new BehaviorSubject<FilterDataResult>(loadingStatus);

    private readonly _reloadCategories$ = new Subject<void>();
    private readonly _reloadSponsors$ = new Subject<void>();

    constructor(
        private readonly _blueCityApi: BlueCityService,
    ) {
        this._reloadCategories$
            .pipe(
                switchMap(() =>

                    /*
                     * Send a loading status first,
                     * then fetch and send the data.
                     */
                    concat(
                        of(loadingStatus),
                        this._createCategoriesOptionsFromAPI$(),
                    ),
                ),
            )
            .subscribe(departmentsResult => {
                this.categoriesOptions$.next(departmentsResult);
            });

        this._reloadSponsors$
            .pipe(
                switchMap(() =>

                    /*
                     * Send a loading status first,
                     * then fetch and send the data.
                     */
                    concat(
                        of(loadingStatus),
                        this._createSponsorsOptionsFromAPI$(),
                    ),
                ),
            )
            .subscribe(categoriesOptions => {
                this.sponsorsOptions$.next(categoriesOptions);
            });

        this._reloadCategories$.next();
        this._reloadSponsors$.next();
    }

    public reloadErrored(): void {
        if (this.categoriesOptions$.value.error) {
            this._reloadCategories$.next();
        }

        if (this.sponsorsOptions$.value.error) {
            this._reloadSponsors$.next();
        }
    }

    private _createCategoriesOptionsFromAPI$(): Observable<FilterDataResult> {
        return this._blueCityApi.getAllBlueCityProjectCategories({})
            .pipe(
                map(result =>
                    result.data
                        .map(category => ({ value: category.id, label: category.name })),
                ),
                toDataResult(),
                catchError(() => of(ToResult.fromErrorKey(FilterDataStatus.Generic))),
            );
    }

    private _createSponsorsOptionsFromAPI$(): Observable<FilterDataResult> {
        return this._blueCityApi.getAllProjectSponsors({})
            .pipe(
                map(result =>
                    result.data
                        .map(sponsor => ({ value: sponsor.id, label: sponsor.name })),
                ),
                toDataResult(),
                catchError(() => of(ToResult.fromErrorKey(FilterDataStatus.Generic))),
            );
    }
}
